/** One audio element per call keeps playback eligible after the learner's start gesture. */
export class FieldSpeechPlayer {
  private readonly audio = new Audio();
  private priming = false;

  constructor() {
    this.audio.preload = 'auto';
  }

  unlock() {
    this.priming = true;
    // A local, silent WAV primes mobile playback without a provider request or microphone access.
    this.audio.src =
      'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQIAAAAAAA==';
    void this.audio
      .play()
      .then(() => {
        if (this.priming) this.audio.pause();
      })
      .catch(() => undefined);
  }

  async play(response: Response, signal: AbortSignal, onPlaying: () => void): Promise<void> {
    if (signal.aborted) throw new DOMException('Playback stopped.', 'AbortError');
    this.priming = false;
    const audio = this.audio;
    // Stop the unlock clip before installing reply callbacks. Its late media events must not
    // mark a downloading fallback reply as speaking or finish it before real audio begins.
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    const pendingEvents = new AbortController();
    let objectUrl: string | undefined;
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    let stopped = false;
    let playbackStarted = false;
    let rejectPlayback: ((cause: unknown) => void) | undefined;
    const playback = new Promise<void>((resolve, reject) => {
      rejectPlayback = reject;
      audio.onplaying = () => {
        if (playbackStarted) onPlaying();
      };
      audio.onended = () => {
        if (playbackStarted) resolve();
      };
      audio.onerror = () => {
        if (objectUrl) reject(new Error('The reply audio could not be played.'));
      };
    });
    void playback.catch(() => undefined);
    const abort = () => {
      stopped = true;
      pendingEvents.abort();
      void reader?.cancel().catch(() => undefined);
      rejectPlayback?.(new DOMException('Playback stopped.', 'AbortError'));
    };
    signal.addEventListener('abort', abort, { once: true });
    const beginPlayback = () => {
      playbackStarted = true;
      void audio.play().catch((cause: unknown) => rejectPlayback?.(cause));
    };
    try {
      const stream = response.body;
      const canStream =
        stream && typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');
      if (canStream) {
        const source = new MediaSource();
        objectUrl = URL.createObjectURL(source);
        const opened = waitForMediaEvent(source, 'sourceopen', pendingEvents.signal);
        audio.src = objectUrl;
        await Promise.race([opened, playback]);
        const buffer = source.addSourceBuffer('audio/mpeg');
        const streamReader = stream.getReader();
        reader = streamReader;
        let started = false;
        const pump = async () => {
          while (!stopped) {
            const { value, done } = await streamReader.read();
            if (done || stopped) break;
            if (!value.byteLength) continue;
            const appended = waitForMediaEvent(buffer, 'updateend', pendingEvents.signal);
            void appended.catch(() => undefined);
            buffer.appendBuffer(new Uint8Array(value).buffer);
            await appended;
            if (!started) {
              started = true;
              beginPlayback();
            }
          }
          if (!stopped && source.readyState === 'open') source.endOfStream();
          if (!started && !stopped) throw new Error('The reply did not contain audio.');
        };
        // Keep reading while play() waits for enough MP3 frames to begin.
        void pump().catch((cause: unknown) => rejectPlayback?.(cause));
      } else {
        const blob = await response.blob();
        if (signal.aborted) throw new DOMException('Playback stopped.', 'AbortError');
        if (!blob.size) throw new Error('The reply did not contain audio.');
        objectUrl = URL.createObjectURL(blob);
        audio.src = objectUrl;
        beginPlayback();
      }
      await playback;
    } finally {
      stopped = true;
      pendingEvents.abort();
      signal.removeEventListener('abort', abort);
      void reader?.cancel().catch(() => undefined);
      audio.onplaying = null;
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  }

  dispose() {
    this.priming = false;
    this.audio.pause();
    this.audio.removeAttribute('src');
    this.audio.load();
  }
}

function waitForMediaEvent(target: EventTarget, event: string, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const clean = () => {
      target.removeEventListener(event, complete);
      target.removeEventListener('error', fail);
      signal.removeEventListener('abort', abort);
    };
    const complete = () => {
      clean();
      resolve();
    };
    const fail = () => {
      clean();
      reject(new Error('The reply audio stream could not be decoded.'));
    };
    const abort = () => {
      clean();
      reject(new DOMException('Playback stopped.', 'AbortError'));
    };
    target.addEventListener(event, complete, { once: true });
    target.addEventListener('error', fail, { once: true });
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
  });
}
